import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';

type Status = 'loading' | 'success' | 'error';

const VerifyEmailPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { user, setUser } = useAuth();

  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Link inválido');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(getApiUrl('/api/auth/confirm-email'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setStatus('error');
          setErrorMsg(data.error || 'Link inválido o expirado');
          return;
        }
        if (user) {
          setUser({ ...user, emailVerified: true });
        }
        setStatus('success');
      } catch {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg('Error de conexión');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, user, setUser]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px 16px' : 32,
        backgroundColor: theme.colors.background,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          padding: 28,
          border: `2px solid ${theme.colors.primary}`,
          textAlign: 'center',
        }}
      >
        {status === 'loading' && (
          <>
            <div
              style={{
                width: 36,
                height: 36,
                margin: '8px auto 16px',
                border: '4px solid transparent',
                borderTop: `4px solid ${theme.colors.primary}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
            <h1 style={{ color: theme.colors.text, fontSize: 20, fontWeight: 700, margin: '8px 0 6px' }}>
              Verificando tu email…
            </h1>
            <p style={{ color: theme.colors.textSecondary, fontSize: 14 }}>
              Esto toma un segundo.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ fontSize: 56, marginBottom: 8 }}>✅</div>
            <h1 style={{ color: theme.colors.text, fontSize: 22, fontWeight: 800, margin: '8px 0 8px', fontFamily: theme.fonts.heading }}>
              ¡Email verificado!
            </h1>
            <p style={{ color: theme.colors.textSecondary, fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
              Tu cuenta está activa. Ya podés armar partidos sin restricciones.
            </p>
            <button
              type="button"
              onClick={() => navigate(user ? '/home' : '/auth')}
              style={{
                width: '100%',
                padding: '14px',
                background: theme.colors.primary,
                color: '#000',
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontSize: theme.fonts.sizeBody,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {user ? 'Ir al inicio' : 'Iniciar sesión'}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ fontSize: 56, marginBottom: 8 }}>⚠️</div>
            <h1 style={{ color: theme.colors.text, fontSize: 22, fontWeight: 800, margin: '8px 0 8px', fontFamily: theme.fonts.heading }}>
              No pudimos verificar
            </h1>
            <p style={{ color: theme.colors.textSecondary, fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
              {errorMsg || 'El link es inválido o expiró. Pedí uno nuevo desde tu perfil.'}
            </p>
            <button
              type="button"
              onClick={() => navigate(user ? '/settings' : '/auth')}
              style={{
                width: '100%',
                padding: '14px',
                background: theme.colors.primary,
                color: '#000',
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontSize: theme.fonts.sizeBody,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {user ? 'Ir a configuración' : 'Iniciar sesión'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
